import {
  FilterDocumentDateCustomTypes,
  FilterDocumentDateFilterTypes,
  GroupByMode,
  ID,
  newTaskSuffix,
  overdueSectionID,
  Task,
} from '@griply/types';
import {
  getNewTaskPrefill,
  getOverdueTasksFromTree,
  getTaskIdFromTreeId,
  getUpcomingSectionDate,
  onUpcomingDeadlineReorder,
} from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTreeValue,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useShouldHaveRestOfMonth } from 'shared/hooks/use-should-have-rest-of-month';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';

import {
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
  useUpcomingDeadlineTree,
} from '../../hooks/use-upcoming-deadline-tree';
import { GroupedListProps } from '../../types';
import { TreeTaskCard } from '../tree-task-card';
import { UpcomingDeadlineHeader } from '../upcoming-deadline-header';
import * as Styled from './upcoming-deadline-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task];

const sectionPrefixes = {
  restOfMonth: upcomingRestOfMonthPrefix,
  month: upcomingMonthPrefix,
  year: upcomingYearPrefix,
};

const isDaySectionId = (sectionId: ID): boolean =>
  sectionId !== overdueSectionID &&
  !sectionId.startsWith(upcomingRestOfMonthPrefix) &&
  !sectionId.startsWith(upcomingMonthPrefix) &&
  !sectionId.startsWith(upcomingYearPrefix);

export const UpcomingDeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();
  const { shouldHaveRestOfMonth, restOfMonthStartDate } =
    useShouldHaveRestOfMonth();

  // Extract dateFilterEndDate from filter document (same pattern as deadline-grouping)
  const dateFilterEndDate = useMemo(() => {
    const directDateFilter = filterDocument?.filters?.date;
    if (
      directDateFilter?.type === FilterDocumentDateFilterTypes.Custom &&
      directDateFilter.custom?.type === FilterDocumentDateCustomTypes.Before
    ) {
      return directDateFilter.custom.end;
    }

    const orBranches = filterDocument?.or ?? [];
    const matchedBranch = orBranches.find(
      (branch) =>
        branch.date?.type === FilterDocumentDateFilterTypes.Custom &&
        branch.date.custom?.type === FilterDocumentDateCustomTypes.Before,
    );

    return matchedBranch?.date?.custom?.end ?? undefined;
  }, [filterDocument]);

  const treeData = useUpcomingDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    dateFilterEndDate,
    hideNewTaskRow: groupSettings?.hideNewTaskRow,
    shouldHaveRestOfMonth,
    restOfMonthStartDate,
  });

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const _onReorder = useCallback(
    (updatedItems: TreeItem[]) => {
      if (!onReorder) {
        return;
      }

      const result = onUpcomingDeadlineReorder({
        updatedItems,
        tasksMap,
        today,
        weekStartsOn,
        prefixes: sectionPrefixes,
      });

      onReorder(result.updatedSections, result.updatedTasks);
    },
    [onReorder, tasksMap, today, weekStartsOn],
  );

  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const displayDue = groupSettings?.displayDue;
  const externalEvents = groupSettings?.externalEvents;
  const timeFormat = groupSettings?.timeFormat;

  const overdueTasks = useMemo(
    () => getOverdueTasksFromTree(treeData, tasksMap),
    [treeData, tasksMap],
  );

  const overdueTasksRef = useRef(overdueTasks);
  overdueTasksRef.current = overdueTasks;

  const externalEventsRef = useRef(externalEvents);
  externalEventsRef.current = externalEvents;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;
          const parentElement = itemInstance.getParent();
          const parentId = parentElement?.getItemData().sourceId;

          const sectionDate = parentId
            ? getUpcomingSectionDate(parentId, sectionPrefixes)
            : undefined;
          const isDay = parentId ? isDaySectionId(parentId) : false;

          // Show due date in overdue section and non-day sections
          const effectiveDisplayDue =
            parentId === overdueSectionID || !isDay ? displayDue : [];

          const taskId = getTaskIdFromTreeId(item.sourceId);

          return currentTasksMap[taskId] ? (
            <TreeTaskCard
              task={currentTasksMap[taskId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              completionDate={isDay ? sectionDate : undefined}
              displayDate={isDay ? sectionDate : undefined}
              displayDue={effectiveDisplayDue}
            />
          ) : null;
        }

        if (type === TaskListItemTypes.NewTask) {
          const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.UpcomingDeadline,
            groupSectionId,
            groupSetting: {},
            today,
          });
          return (
            <NewTaskRow
              date={prefill.startDate}
              startTime={null}
              duration={null}
            />
          );
        }

        if (type === TaskListItemTypes.Date) {
          return (
            <UpcomingDeadlineHeader
              denseFormat={denseFormat}
              item={itemInstance}
              overdueTasks={overdueTasksRef.current}
              externalEvents={externalEventsRef.current}
              timeFormat={timeFormat}
            />
          );
        }

        return null;
      },
    [denseFormat, displayDue, timeFormat, today],
  );

  const tree = useTreeValue();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
